import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/company/
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: convex _generated
import { useCompanyContext } from '@/hooks/useCompanyContext';
import { useCompanyRole } from '@/hooks/useCompanyRole';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Users, Plus, Trash2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { ImportEmployeeDialog } from '@/components/company/ImportEmployeeDialog';

function EmployeeTableSkeleton() {
  return (
    <>
      {[...Array(5)].map((_, i) => (
        <div key={i} className="flex items-center px-6 py-3 border-b border-table-row-border gap-4">
          <Skeleton className="h-4 w-36" />
          <Skeleton className="h-4 w-44 ml-4" />
          <Skeleton className="h-4 w-28 ml-4" />
          <Skeleton className="h-4 w-24 ml-4" />
          <Skeleton className="h-4 w-10 ml-4" />
          <Skeleton className="h-4 w-24 ml-auto" />
        </div>
      ))}
    </>
  );
}

function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function EmployeesListPage() {
  const navigate = useNavigate();
  const { currentCompany, isLoading: companyLoading } = useCompanyContext();
  const { canEditEmployees, isAdmin } = useCompanyRole();
  const [searchQuery, setSearchQuery] = useState('');
  const [importOpen, setImportOpen] = useState(false);

  const rawEmployees = useQuery(
    api.companies.employees.list,
    currentCompany ? { companyId: currentCompany._id } : 'skip'
  );
  const removeEmployee = useMutation(api.companies.employees.remove);

  const isLoading = companyLoading || rawEmployees === undefined;

  const filtered = useMemo(() => {
    const list = rawEmployees ?? [];
    if (!searchQuery.trim()) return list;
    const q = searchQuery.toLowerCase();
    return list.filter(
      e =>
        e.name.toLowerCase().includes(q) ||
        e.email?.toLowerCase().includes(q) ||
        e.phoneNumber?.toLowerCase().includes(q)
    );
  }, [rawEmployees, searchQuery]);

  const handleDelete = useCallback(
    async (e: React.MouseEvent, employeeId: Id<'companyEmployees'>) => {
      e.stopPropagation();
      try {
        await removeEmployee({ employeeId });
        toast.success('Employee removed');
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to remove employee');
      }
    },
    [removeEmployee]
  );

  if (!companyLoading && !currentCompany) {
    return (
      <div className="min-h-full bg-background-secondary py-8 px-10">
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <Users className="w-12 h-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-1">No company found</h3>
          <p className="text-sm text-muted-foreground">
            You need to be part of a company to manage employees.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background-secondary py-8 px-10">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Employees</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {isLoading ? 'Loading...' : `${rawEmployees?.length ?? 0} total`}
          </p>
        </div>
        {canEditEmployees && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setImportOpen(true)}>
              <Sparkles aria-hidden="true" className="w-4 h-4 mr-2" />
              Import with AI
            </Button>
            <Button onClick={() => navigate('/company/employees/new')}>
              <Plus aria-hidden="true" className="w-4 h-4 mr-2" />
              Add Employee
            </Button>
          </div>
        )}
      </div>

      <div className="bg-table-container border border-table-container-border rounded-2xl overflow-hidden shadow">
        <div className="flex items-center gap-3 px-6 py-3 border-b border-table-row-border">
          <label htmlFor="employee-search" className="sr-only">
            Search employees
          </label>
          <Input
            id="employee-search"
            placeholder="Search by name, email or phone..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="max-w-xs"
          />
        </div>

        {/* Table header */}
        <div className="flex items-center px-6 py-2 border-b border-table-row-border bg-muted/30 text-xs font-medium text-muted-foreground uppercase tracking-wider">
          <span className="w-48 shrink-0">Name</span>
          <span className="w-52 shrink-0">Email</span>
          <span className="w-36 shrink-0">Phone</span>
          <span className="w-28 shrink-0">Position</span>
          <span className="w-20 shrink-0">Policies</span>
          <span className="flex-1">Created</span>
          {isAdmin && <span className="w-20 shrink-0 text-right">Actions</span>}
        </div>

        <div className="overflow-auto">
          {isLoading && <EmployeeTableSkeleton />}

          {!isLoading &&
            filtered.map(employee => (
              <div
                key={employee._id}
                className="flex items-center px-6 py-3 border-b border-table-row-border hover:bg-muted/50 cursor-pointer"
                onClick={() => navigate(`/company/employees/${employee._id}`)}
              >
                <span className="w-48 shrink-0 text-sm font-medium text-foreground truncate pr-3">
                  {employee.name}
                </span>
                <span className="w-52 shrink-0 text-sm text-muted-foreground truncate pr-3">
                  {employee.email ?? '—'}
                </span>
                <span className="w-36 shrink-0 text-sm text-muted-foreground truncate pr-3">
                  {employee.phoneNumber ?? '—'}
                </span>
                <span className="w-28 shrink-0 text-sm text-muted-foreground truncate pr-3">
                  {employee.position ?? '—'}
                </span>
                <span className="w-20 shrink-0 text-sm text-muted-foreground">
                  {employee.policyCount}
                </span>
                <span className="flex-1 text-sm text-muted-foreground">
                  {formatDate(employee.createdAt)}
                </span>
                {isAdmin && (
                  <div className="w-20 shrink-0 flex justify-end">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10 h-7 w-7"
                      onClick={e => handleDelete(e, employee._id)}
                      aria-label={`Delete ${employee.name}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            ))}

          {!isLoading && filtered.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Users className="w-12 h-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-1">No employees found</h3>
              <p className="text-sm text-muted-foreground">
                {searchQuery
                  ? 'Try adjusting your search query'
                  : 'Add your first employee to get started'}
              </p>
            </div>
          )}
        </div>
      </div>
      {currentCompany && (
        <ImportEmployeeDialog
          open={importOpen}
          onOpenChange={setImportOpen}
          companyId={currentCompany._id}
        />
      )}
    </div>
  );
}
